import { AutomationVersionStrategy } from "@redotech/crdb-enums/automations/automation-version-strategy";
import { RedoAutomationsRpcClient } from "@redotech/redo-merchant-automations-rpc/client";
import { scenarioSchema } from "@redotech/redo-merchant-automations-rpc/schema/simulate";
import type { AutomationsApi, VersionStrategy } from "./automations-api";

function strategyOf(strategy: AutomationVersionStrategy): VersionStrategy {
  switch (strategy) {
    case AutomationVersionStrategy.Latest:
      return "latest";
    case AutomationVersionStrategy.Pinned:
      return "pinned";
  }
}

function rpcStrategy(strategy: VersionStrategy): AutomationVersionStrategy {
  switch (strategy) {
    case "latest":
      return AutomationVersionStrategy.Latest;
    case "pinned":
      return AutomationVersionStrategy.Pinned;
  }
}

function summary<T extends { versionStrategy: AutomationVersionStrategy }>(
  automation: T,
): Omit<T, "versionStrategy"> & { versionStrategy: VersionStrategy } {
  return {
    ...automation,
    versionStrategy: strategyOf(automation.versionStrategy),
  };
}

/** AutomationsApi over the `/automations-rpc` client. */
export function rpcAutomationsApi(
  client: RedoAutomationsRpcClient,
): AutomationsApi {
  return {
    listAutomations: async () =>
      (await client.listAutomations({})).automations.map(summary),
    getAutomation: async (id) =>
      summary((await client.getAutomation({ id })).automation),
    createAutomation: async (input) => {
      const result = await client.createAutomation(input);
      return { ...result, automation: summary(result.automation) };
    },
    saveDraft: async (id, draft, draftRevision) => {
      const result = await client.saveDraft({ id, draft, draftRevision });
      return { ...result, automation: summary(result.automation) };
    },
    previewPublish: (id, draftRevision) =>
      client.previewPublish({ id, draftRevision }),
    publish: async (id, draftRevision) => {
      const result = await client.publish({ id, draftRevision });
      return result.ok
        ? {
            ok: true,
            automation: summary(result.automation),
            version: result.version.version,
          }
        : result;
    },
    setActive: async (id, active) =>
      summary((await client.setActive({ id, active })).automation),
    setVersionStrategy: async (id, strategy) =>
      summary(
        (
          await client.setVersionStrategy({
            id,
            versionStrategy: rpcStrategy(strategy),
          })
        ).automation,
      ),
    deleteAutomation: async (id) => {
      await client.deleteAutomation({ id });
    },
    listVersions: async (id) => (await client.listVersions({ id })).versions,
    getVersion: async (id, version) =>
      (await client.getVersion({ id, version })).version.definition,
    restoreVersion: async (id, version, draftRevision) => {
      const result = await client.restoreVersion({
        id,
        version,
        draftRevision,
      });
      return { ...result, automation: summary(result.automation) };
    },
    validate: async (definition) =>
      (await client.validate({ definition })).diagnostics,
    simulate: (definition, scenario) =>
      client.simulate({ definition, scenario: scenarioSchema.parse(scenario) }),
    explore: (definition) => client.explore({ definition }),
    catalog: (triggerKind) =>
      client.getCatalog(triggerKind === undefined ? {} : { triggerKind }),
    runNow: (input) => client.runNow(input),
    listRuns: async (id) => (await client.listRuns({ id })).runs,
    getRun: async (runId) => (await client.getRun({ runId })).run,
  };
}
